import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  Button,
  IconButton,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import userService from '../services/userService';

const ViewRelatedProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [productName, setProductName] = useState(null);
  
  const ITEMS_PER_PAGE = 12;
  
  // Determine product type from route path
  const productType = location.pathname.includes('/product/') ? 'product' : 'otc';

  useEffect(() => {
    if (id) {
      fetchRelatedProducts();
    }
  }, [id, productType]);

  const fetchRelatedProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (productType === 'product') {
        response = await userService.getSameSaltProducts(id);
      } else {
        response = await userService.getSameCategoryOTCs(id);
      }

      if (response.data) {
        // Handle edge cases
        if (response.data.message) {
          // Product without salt composition or OTC without category
          setProducts([]);
        } else if (response.data.results && Array.isArray(response.data.results)) {
          setProducts(response.data.results);
          // Store category name for OTC products
          if (productType === 'otc' && response.data.category) {
            setCategoryName(response.data.category.name);
          }
          // Store product name
          if (productType === 'product' && response.data.product_name) {
            setProductName(response.data.product_name);
          } else if (productType === 'otc' && response.data.otc_name) {
            setProductName(response.data.otc_name);
          }
        } else {
          setProducts([]);
        }
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Error fetching related products:', err);
      setError('Failed to load related products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const getProductImage = (images, isOTC = false) => {
    if (!images) {
      return '/logo192.png';
    }

    if (Array.isArray(images)) {
      return images[0] || '/logo192.png';
    } else {
      // Handle string format
      if (isOTC) {
        const imageNames = images.split(',').map(img => img.trim()).filter(img => img);
        return imageNames[0] ? `/assets/images_1mg_otc/${imageNames[0]}` : '/logo192.png';
      } else {
        const imageNames = images.split(',').map(img => img.trim()).filter(img => img);
        return imageNames[0] ? `/assets/Drugs Images/${imageNames[0]}` : '/logo192.png';
      }
    }
  };

  const handleProductClick = (productId) => {
    if (productType === 'product') {
      navigate(`/product/${productId}`);
    } else {
      navigate(`/otc/${productId}`);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackClick = () => {
    if (productType === 'product') {
      navigate(`/product/${id}`);
    } else {
      navigate(`/otc/${id}`);
    }
  };

  const getPageTitle = () => {
    if (productType === 'product') {
      return 'Similar Products';
    } else {
      return categoryName ? `More from ${categoryName}` : 'Similar Products';
    }
  };

  // Calculate products to display for current page
  const getDisplayedProducts = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return products.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!products || products.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBackClick} sx={{ ml: -1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            {getPageTitle()}
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          No related products found.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
        >
          Go Back to Product
        </Button>
      </Container>
    );
  }

  const displayedProducts = getDisplayedProducts();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Back Button */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleBackClick} sx={{ ml: -1 }}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {getPageTitle()}
          </Typography>
          {productName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Related to: {productName}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Products Count */}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Showing {displayedProducts.length} of {products.length} products
      </Typography>

      {/* Products Grid */}
      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
        {displayedProducts.map((product) => (
          <Grid
            item
            xs={6}
            sm={6}
            md={4}
            key={product.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: '160px', sm: '170px', md: '180px' },
              maxWidth: { xs: '100%', sm: '100%', md: '400px' },
            }}
          >
            <Card
              onClick={() => handleProductClick(product.id)}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
                  borderColor: 'primary.main',
                },
              }}
            >
              {/* Product Image */}
              <Box
                sx={{
                  width: '100%',
                  height: { xs: '120px', sm: '140px', md: '160px' },
                  backgroundColor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                <CardMedia
                  component="img"
                  image={getProductImage(product.images, productType === 'otc')}
                  alt={product.name}
                  sx={{
                    objectFit: 'contain',
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                  onError={(e) => {
                    e.target.src = '/logo192.png';
                  }}
                />
              </Box>

              {/* Product Info */}
              <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Product Name */}
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                    fontWeight: 500,
                    color: 'text.primary',
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: '2.5em',
                  }}
                >
                  {product.name}
                </Typography>

                {/* Manufacturer */}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.8rem' },
                    color: 'text.secondary',
                    mb: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {product.manufacturer}
                </Typography>

                {/* Category/Sub-category for OTC */}
                {productType === 'otc' && (
                  <>
                    {product.category && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' },
                          color: 'text.secondary',
                          mb: 0.5,
                        }}
                      >
                        {product.category}
                      </Typography>
                    )}
                    {product.sub_category && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' },
                          color: 'primary.main',
                          mb: 1,
                        }}
                      >
                        {product.sub_category}
                      </Typography>
                    )}
                  </>
                )}

                {/* Salt Composition for Products */}
                {productType === 'product' && product.salt_composition && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' },
                      color: 'text.secondary',
                      mb: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {product.salt_composition}
                  </Typography>
                )}

                {/* Price Section */}
                <Box
                  sx={{
                    mt: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                      color: 'primary.main',
                      fontWeight: 600,
                    }}
                  >
                    ₹{product.best_price || product.price}
                  </Typography>
                  {product.best_price && product.best_price !== product.price && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: 'line-through',
                          color: 'text.secondary',
                          fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                        }}
                      >
                        ₹{product.price}
                      </Typography>
                      {product.discount_percent && (
                        <Chip
                          label={`${product.discount_percent}% OFF`}
                          color="success"
                          size="small"
                          sx={{
                            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                            height: { xs: '20px', sm: '22px', md: '24px' },
                          }}
                        />
                      )}
                    </>
                  )}
                </Box>

                {/* Prescription Required Badge */}
                {product.prescription_required && (
                  <Chip
                    label="Prescription Required"
                    color="warning"
                    size="small"
                    sx={{
                      mt: 1,
                      fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.7rem' },
                      height: { xs: '18px', sm: '20px', md: '22px' },
                    }}
                  />
                )}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Container>
  );
};

export default ViewRelatedProducts;

